import { Dispatch, DragEvent, SetStateAction } from 'react';
import { TrackFilterParams, TrackObj, TrackVersionsObj } from '../../pages/MyTracks/types';
import {
  Collaborator,
  Folder,
  GetUserResponse,
  ProjectFile,
  Track,
  TrackVersion,
  VersionState,
} from '../../proto/api_pb';
import { TrackToPlay } from '../../pages/ExplorePage/types';
import { OpenFoldersListType } from '../../pages/Tracks/types';
import WaveSurfer from 'wavesurfer.js';

export const userPages = [
  {
    name: 'Dashboard',
    route: '/dashboard',
    iconPath: '/assets/icons/TracksPageIcons/home-icon.svg',
  },
  {
    name: 'Hub',
    route: '/my-tracks',
    iconPath: '/assets/icons/TracksPageIcons/music-note-02.svg',
  },
  {
    name: 'Projects',
    route: '/projects',
    iconPath: '/assets/icons/TracksPageIcons/grid-01.svg',
  },
  {
    name: 'Explore',
    route: '/explore',
    iconPath: '/assets/icons/TracksPageIcons/compass-03.svg',
  },
];

export interface TracksPageLeftSideDrawerProps {
  currentUser: GetUserResponse | undefined;
  isDrawerOpen?: boolean;
  setIsDrawerOpen?: Dispatch<SetStateAction<boolean>>;
}

export interface FilterMenuProps {
  allArtistList: string[];
  collaboratorsOptionsList: string[];
  filterAndSortParams: TrackFilterParams | undefined;
  value: number;
  setFilterAndSortParams: Dispatch<SetStateAction<TrackFilterParams>>;
  handleFiltersAndSort: () => Promise<void>;
  setPage: Dispatch<SetStateAction<number>>;
  setTracks: Dispatch<SetStateAction<TrackObj[]>>;
}

export interface SortMenuProps {
  setFilterAndSortParams: Dispatch<SetStateAction<TrackFilterParams>>;
  filterAndSortParams: TrackFilterParams;
  setPage: Dispatch<SetStateAction<number>>;
  setFilterAndSortChanged: Dispatch<SetStateAction<boolean>>;
  setTracks: Dispatch<SetStateAction<TrackObj[]>>;
  handleRefreshTracks: () => Promise<void>;
}
export interface TrackInfo {
  title: string;
  group: string;
  image: string;
  moreDetails: Track;
  id: string;
}

export interface TrackPlayPauseProps {
  track: TrackObj;
  handlePlayButtonClick: () => void;
  setTrackToPlay: Dispatch<SetStateAction<TrackToPlay | undefined>>;
  setIsTrackForPlay: Dispatch<SetStateAction<boolean>>;
  trackToPlay: TrackToPlay | undefined;
  setVersionToPlay: Dispatch<SetStateAction<TrackVersionsObj | undefined>>;
  size?: number;
  /** Optional colored border around the artwork/logo image (Hub list row). */
  frameColor?: string;
  /** Optional border width for the artwork/logo image. */
  borderWidth?: string;
}

export interface TrackListViewProps {
  track: TrackObj;
  index: number;
  isBoxChecked: (item: TrackObj) => boolean;
  handleCheckedTrackChange: (track: TrackObj, id: string) => void;
  handleRefreshTracks: () => Promise<void>;
  handleOpenTrackVersionModal: (tr: Track, latestVersionNumber: number) => void;
  handleOpenCollabsModal: (t: TrackObj) => void;
  handleOpenSelectDeleteModal: (t: TrackObj) => void;
  handleOpenEditTrackModal: (tr: TrackObj) => void;
  setIsPlayButtonClicked: Dispatch<SetStateAction<boolean>>;
  trackToPlay: TrackToPlay | undefined;
  setTrackToPlay: Dispatch<SetStateAction<TrackToPlay | undefined>>;
  setIsTrackForPlay: Dispatch<SetStateAction<boolean>>;
  setVersionToPlay: Dispatch<SetStateAction<TrackVersionsObj | undefined>>;
  setAudioDetails: Dispatch<SetStateAction<AudioDetails | undefined>>;
  handleExpandTrack: (trackId: string) => void;
  expanded: boolean;
  handleDragStart: (index: number) => void;
  handleDrop: (e: DragEvent<HTMLDivElement>, dropId: number) => void;
  setDraggedIndex: Dispatch<SetStateAction<number | null>>;
  isReorderAvailable: boolean;
  handleOpenUpdateSubModal: () => void;
  versionToPlay: TrackVersionsObj | undefined;
  isAddFolderToProject: boolean;
  handleApplyFilterAndSort: () => Promise<void>;
  shareToken: string | undefined;
  allowDownload: boolean | undefined;
}

export interface TracksGridViewProps {
  tracks: TrackObj[] | undefined;
  isGridView: boolean;
  setAudioTrack: (id: string, audioTrack: WaveSurfer) => void;
  setTracks: Dispatch<SetStateAction<TrackObj[]>>;
  isBoxChecked: (item: TrackObj) => boolean;
  handleCheckedTrackChange: (track: TrackObj, id: string) => void;
  handleRefreshTracks: () => Promise<void>;
}

export interface TrackMenuProps {
  track: TrackObj;
  handleOpenCollabsModal?: (t: TrackObj) => void;
  handleOpenEditTrackModal?: (t: TrackObj) => void;
  handleOpenSelectDeleteModal?: (t: TrackObj) => void;
  handleRefreshTracks: () => Promise<void>;
  handleOpenUpdateSubModal: () => void;
  allowDownload: boolean | undefined;
  /** Current publish state — drives the Make public/private menu item. */
  isPublished?: boolean;
  /** Toggles publish/private (replaces the former inline row Switch). */
  onTogglePublic?: () => void;
}

export interface FilterAndSortDetailsProps {
  filterAndSortParams: TrackFilterParams;
  setFilterAndSortParams: Dispatch<SetStateAction<TrackFilterParams>>;
  setPage: Dispatch<SetStateAction<number>>;
  setTracks: Dispatch<SetStateAction<TrackObj[]>>;
  queryValue: string;
  setQueryValue: Dispatch<SetStateAction<string>>;
  value: number;
}

export interface AddMusicSectionProps {
  handleOpenBatchTrackAddModal: () => void;
  handleOpenUpdateSubModal: () => void;
}

export const versionState: Record<number, string> = {
  [VersionState.VERSION_STATE_UNSPECIFIED]: 'Select status',
  [VersionState.VERSION_STATE_READY_FOR_RECORDING]: 'Ready for recording',
  [VersionState.VERSION_STATE_READY_FOR_MIXING]: 'Ready for mixing',
  [VersionState.VERSION_STATE_READY_FOR_MASTERING]: 'Ready for mastering',
  [VersionState.VERSION_STATE_READY_FOR_PUBLISHING]: 'Ready for publishing',
  [VersionState.VERSION_STATE_COMPLETED]: 'Completed track',
  [VersionState.VERSION_STATE_PRE_PRODUCTION]: 'Pre production',
};

export interface AccordionDetailsProps {
  track: TrackObj;
  handlePlayButtonClick: () => void;
  setVersionToPlay: Dispatch<SetStateAction<TrackVersionsObj | undefined>>;
  setIsTrackForPlay: Dispatch<SetStateAction<boolean>>;
  setAudioDetails: Dispatch<SetStateAction<AudioDetails | undefined>>;
  setLatestVersion: Dispatch<SetStateAction<TrackVersion | undefined>>;
  handleOpenTrackVersionModal: (tr: Track, latestVersionNumber: number) => void;
  expanded: boolean;
  handleRefreshTracks: () => Promise<void>;
  versionToPlay: TrackVersionsObj | undefined;
  setTrackToPlay: Dispatch<SetStateAction<TrackToPlay | undefined>>;
  handleApplyFilterAndSort: () => Promise<void>;
  shareToken: string | undefined;
}

export interface AudioPlayerProps {
  trackToPlay: TrackToPlay | undefined;
  isPlayButtonClicked: boolean;
  versionToPlay: TrackVersionsObj | undefined;
  isTrackForPlay: boolean;
  audioDetails: AudioDetails | undefined;
  setTrackToPlay: Dispatch<SetStateAction<TrackToPlay | undefined>>;
  setIsPlayButtonClicked: Dispatch<SetStateAction<boolean>>;
  setVersionToPlay?: Dispatch<SetStateAction<TrackVersionsObj | undefined>>;
}

export interface AudioDetails {
  title: string;
  image: string;
}

export enum VMap {
  VERSION_STATE_UNSPECIFIED = 0,
  VERSION_STATE_READY_FOR_RECORDING = 1,
  VERSION_STATE_READY_FOR_MIXING = 2,
  VERSION_STATE_READY_FOR_MASTERING = 3,
  VERSION_STATE_READY_FOR_PUBLISHING = 4,
  VERSION_STATE_COMPLETED = 5,
  VERSION_STATE_PRE_PRODUCTION = 6,
}

export interface StatusAndNoteProps {
  version: TrackVersion;
  setIsVStateReadyForMastering?: Dispatch<SetStateAction<boolean>>;
  handleFetchTrackVersions?: () => Promise<void>;
  handleRefreshTracks: () => Promise<void>;
  track: Track;
  handleApplyFilterAndSort: () => Promise<void>;
  /** Controls whether to render the inline version note/label alongside the status pill. Defaults to true. */
  showVersionLabel?: boolean;
}

export interface TrackListModalGroupProps {
  track: TrackObj;
  isVStateReadyForMastering: boolean;
}

export interface TracksSearchBarProps {
  setTracks: Dispatch<SetStateAction<TrackObj[]>>;
  setPage: Dispatch<SetStateAction<number>>;
  queryValue: string;
  setQueryValue: Dispatch<SetStateAction<string>>;
  setFilterAndSortChanged: Dispatch<SetStateAction<boolean>>;
}

export interface FilesSectionProps {
  isAllFilesSelected: boolean;
  setIsAllFilesSelected: Dispatch<SetStateAction<boolean>>;
  setCheckedFiles: Dispatch<SetStateAction<ProjectFile[]>>;
  isFileChecked: (item: ProjectFile) => boolean;
  handleCheckedFileChange: (file: ProjectFile, id: string) => void;
  projectQueryValue: string;
  filter: string;
  filesSortBy: 0 | 1 | 2;
  filesSortingIsAscending: boolean;
  setFilesSortingIsAscending: Dispatch<SetStateAction<boolean>>;
  folderId: string | undefined;
  foldersLength: number | undefined;
  isAddFolderToProject: boolean;
  isFilesUpdated: boolean;
  setIsFilesUpdated: Dispatch<SetStateAction<boolean>>;
  setIsCountUpdated?: (value: boolean) => void;
  getStorageUsed: () => Promise<void>;
}

export enum PRIVACY_OPTIONS {
  ALL = 'All',
  PUBLIC = 'Public',
  PROVATE = 'Private',
}

export interface CollaboratorsProps {
  collaborators: Collaborator[];
  isGridView: boolean;
}

export interface TracksTableHeadingsProps {
  filterAndSortParams: TrackFilterParams;
  handleAscendingOrDescending: () => void;
  isAllTracksSelected: boolean;
  setCheckedTracks: Dispatch<SetStateAction<TrackObj[]>>;
  tracks: TrackObj[];
  setIsAllTracksSelected: Dispatch<SetStateAction<boolean>>;
  isAddFolderToProject: boolean;
}

/**
 * Props for the Hub redesign's `TrackListHeader`. Diverges from the legacy
 * public-share `TracksTableHeadings` (above): its column labels are clickable
 * sort targets, so it takes a column-aware `handleSort(sortKey)` instead of the
 * legacy direction-only `handleAscendingOrDescending`.
 */
export interface TrackListHeaderProps {
  filterAndSortParams: TrackFilterParams;
  handleSort: (sortKey: string) => void;
  isAllTracksSelected: boolean;
  setCheckedTracks: Dispatch<SetStateAction<TrackObj[]>>;
  tracks: TrackObj[];
  setIsAllTracksSelected: Dispatch<SetStateAction<boolean>>;
  isAddFolderToProject: boolean;
}

export interface FolderSectionProps {
  folders: Folder[];
  isLoading: boolean;
  setOpenFoldersList: Dispatch<SetStateAction<OpenFoldersListType[]>> | undefined;
  setTracks: Dispatch<SetStateAction<TrackObj[]>>;
  setValue: Dispatch<SetStateAction<number>>;
  value: number;
  getAllFoldersForProject: () => Promise<void>;
  selectedFolders: string[];
  setSelectedFolders: Dispatch<SetStateAction<string[]>>;
  isAddFolderToProject: boolean;
  onFolderClick?: (folderId: string) => void;
  isCountUpdated?: boolean;
  setIsCountUpdated?: (value: boolean) => void;
  bulkActionMode?: 'create' | 'edit' | 'delete' | null;
  selectedFoldersForProjects?: Set<string>;
  onFolderCheckboxChange?: (folderId: string, checked: boolean) => void;
}
export interface TotalTrackCountProps {
  getValidAccessToken: () => Promise<string>;
  userId: string;
  enterpriseId?: string;
  isCountUpdated?: boolean;
  setIsCountUpdated?: (value: boolean) => void;
}
export interface AssetCountProps {
  projectId?: string;
  folderId?: string;
  isCountUpdated?: boolean;
  setIsCountUpdated?: (value: boolean) => void;
}
